import { useCallback, useState } from "react";

import type { StackletGridPagination } from "app/components/table/StackletGrid";

/** Default rows shown per page in an expanded section. */
export const SECTION_PAGE = 5;

/** One expanded section's current page: a cursor window plus its size. */
export interface SectionPage {
  pageSize: number;
  first: number | null;
  last: number | null;
  after: string | null;
  before: string | null;
  pageIndex: number;
}

/** The first page for a freshly expanded section, at the given size. */
export const firstPage = (pageSize = SECTION_PAGE): SectionPage => ({
  pageSize,
  first: pageSize,
  last: null,
  after: null,
  before: null,
  pageIndex: 0,
});

/** Per-render inputs for {@link SectionPagination.getPager}. */
export interface SectionPagerOptions {
  /** The loaded page's cursor flags. */
  pageInfo: {
    hasNextPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
    total: number | null;
  };
  /** Rows on the current page, for the footer's range display. */
  rowCount: number;
  /** Singular noun for the summary, e.g. "user" / "group". */
  unit: string;
  /** Selectable page sizes; omit to use the pager footer's default. */
  pageSizeOptions?: readonly number[];
}

export interface SectionPaginationOptions {
  /** Rows per page a fresh section starts at. Defaults to {@link SECTION_PAGE}. */
  pageSize?: number;
  /**
   * Makes the page size grid-wide: a pager's size selector resets every
   * section to its first page at the new size and reports it here (e.g. to
   * persist it), instead of resizing only its own section.
   */
  onPageSizeChange?: (size: number) => void;
}

export interface SectionPagination<TData> {
  /** Ids of the open sections. */
  expanded: ReadonlySet<string>;
  /** The current page window per open section. */
  pageById: Record<string, SectionPage>;
  /** The loaded data per section that has reported a page. */
  dataById: Record<string, TData>;
  /** Sections whose next page is in flight. */
  loadingById: Record<string, boolean>;
  /** Opens/closes a section, seeding its first page on open. */
  toggle: (id: string) => void;
  /** Advances a section to the next page, from its end cursor. */
  goToNextPage: (id: string, endCursor: string | null) => void;
  /** Steps a section back a page, from its start cursor. */
  goToPrevPage: (id: string, startCursor: string | null) => void;
  /** Resets a section to its first page at a new size. */
  setPageSize: (id: string, size: number) => void;
  /** Records a section's loaded page and clears its loading flag. */
  onLoaded: (id: string, data: TData) => void;
  /**
   * Builds the section's {@link StackletGridPagination} footer descriptor from
   * its current page and the loaded page info, or null when it fits one page.
   */
  getPager: (
    id: string,
    options: SectionPagerOptions,
  ) => StackletGridPagination | null;
}

/**
 * Per-section cursor pagination for an accordion grid: which sections are open,
 * each one's current page window, its loaded data, and whether a page is in
 * flight. The caller fetches each open section's page (from `pageById`) and
 * reports it back via `onLoaded`. Generic over the section's data shape.
 */
export function useSectionPagination<TData>({
  pageSize = SECTION_PAGE,
  onPageSizeChange,
}: SectionPaginationOptions = {}): SectionPagination<TData> {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [pageById, setPageById] = useState<Record<string, SectionPage>>({});
  const [dataById, setDataById] = useState<Record<string, TData>>({});
  const [loadingById, setLoadingById] = useState<Record<string, boolean>>({});

  const startLoading = useCallback((id: string) => {
    setLoadingById((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  }, []);

  const toggle = useCallback(
    (id: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      // Seed the first page on open. Idempotent, so running it on collapse too is
      // harmless — and it keeps `toggle` off an `expanded` dep, so it stays stable
      // and callers' memoised columns don't rebuild on every toggle.
      setPageById((prev) =>
        prev[id] ? prev : { ...prev, [id]: firstPage(pageSize) },
      );
    },
    [pageSize],
  );

  const goToNextPage = useCallback(
    (id: string, endCursor: string | null) => {
      startLoading(id);
      setPageById((prev) => {
        const cur = prev[id] ?? firstPage(pageSize);
        return {
          ...prev,
          [id]: {
            ...cur,
            first: cur.pageSize,
            last: null,
            after: endCursor,
            before: null,
            pageIndex: cur.pageIndex + 1,
          },
        };
      });
    },
    [startLoading, pageSize],
  );

  const goToPrevPage = useCallback(
    (id: string, startCursor: string | null) => {
      startLoading(id);
      setPageById((prev) => {
        const cur = prev[id] ?? firstPage(pageSize);
        return {
          ...prev,
          [id]: {
            ...cur,
            first: null,
            last: cur.pageSize,
            after: null,
            before: startCursor,
            pageIndex: Math.max(0, cur.pageIndex - 1),
          },
        };
      });
    },
    [startLoading, pageSize],
  );

  const setPageSize = useCallback(
    (id: string, size: number) => {
      startLoading(id);
      setPageById((prev) => ({ ...prev, [id]: firstPage(size) }));
    },
    [startLoading],
  );

  // The grid-wide resize: every known section back to its first page at the
  // new size, flagged loading until its refetched page lands. A section that
  // has only reported its initial page (never toggled or paged, so absent from
  // `pageById`) is known through `dataById` alone; leaving it out would keep
  // its pager live over stale rows while it refetches, so a Next click could
  // send the old page's cursor with the new size.
  const resizeAll = useCallback(
    (size: number) => {
      const ids = [
        ...new Set([...Object.keys(pageById), ...Object.keys(dataById)]),
      ];
      setLoadingById((prev) => ({
        ...prev,
        ...Object.fromEntries(ids.map((id) => [id, true])),
      }));
      setPageById(Object.fromEntries(ids.map((id) => [id, firstPage(size)])));
    },
    [pageById, dataById],
  );

  const onLoaded = useCallback((id: string, data: TData) => {
    setDataById((prev) => (prev[id] === data ? prev : { ...prev, [id]: data }));
    setLoadingById((prev) => (prev[id] ? { ...prev, [id]: false } : prev));
  }, []);

  const getPager = useCallback(
    (
      id: string,
      { pageInfo, rowCount, unit, pageSizeOptions }: SectionPagerOptions,
    ): StackletGridPagination | null => {
      const page = pageById[id] ?? firstPage(pageSize);
      const { total } = pageInfo;
      // Derive from page index + total, which are stable across a swap. The
      // loaded page's `hasNextPage` lags a click, so using it blinks the footer.
      const hasPreviousPage = page.pageIndex > 0;
      const hasNextPage =
        total != null
          ? (page.pageIndex + 1) * page.pageSize < total
          : pageInfo.hasNextPage;
      // Show the pager whenever the section has rows (even on one page), so
      // raising the page size to fit everything can't strand its size selector.
      if (rowCount === 0) return null;

      return {
        pageSize: page.pageSize,
        pageSizeOptions,
        // Re-selecting the active size would refetch identical vars, so Relay
        // returns the same snapshot and the loading flag would never clear.
        onPageSizeChange: (size) => {
          if (size === page.pageSize) return;
          if (onPageSizeChange) {
            resizeAll(size);
            onPageSizeChange(size);
          } else {
            setPageSize(id, size);
          }
        },
        hasNextPage,
        hasPreviousPage,
        onNextPage: () => goToNextPage(id, pageInfo.endCursor),
        onPreviousPage: () => goToPrevPage(id, pageInfo.startCursor),
        rangeStart: page.pageIndex * page.pageSize + 1,
        rangeEnd: page.pageIndex * page.pageSize + rowCount,
        total,
        unit,
        isDisabled: Boolean(loadingById[id]),
      };
    },
    [
      pageById,
      loadingById,
      pageSize,
      goToNextPage,
      goToPrevPage,
      setPageSize,
      resizeAll,
      onPageSizeChange,
    ],
  );

  return {
    expanded,
    pageById,
    dataById,
    loadingById,
    toggle,
    goToNextPage,
    goToPrevPage,
    setPageSize,
    onLoaded,
    getPager,
  };
}
